import React, { useEffect, useRef, useState } from "react";
import { API_URL } from "@client/utils/apiUrl";
import { getFoscamIce } from "@client/api/foscam";
import { VideoRTC } from "@client/vendor/go2rtc/video-rtc";

/**
 * Přehrávač jedné kamery přes go2rtc **WebSocket API** (`/api/ws`) s **trickle ICE**.
 *
 * Proč WS a ne ruční WHEP: WHEP je synchronní (offer→answer jednorázově). Náš dřívější ruční
 * WHEP klient čekal na ICE gathering jen 2 s a pak poslal offer natvrdo — 4 kamery startující
 * naráz bombardovaly coturn a u pomalejších dvou relay candidate nedoběhl do 2 s → za NAT žádná
 * cesta → černá → HLS. go2rtc WS umí **asynchronní trickle**: kandidáti (relay/srflx/host) tečou
 * jak se gatherují, žádný závod. Jeden mechanismus pokryje všechny přístupové cesty stejně:
 *  - **LAN** (LAN IP / demirel.local): přímá dvojice s `192.168.x:8555` (host, bez coturnu),
 *  - **tailnet**: přímá dvojice s `100.64.0.2:8555`,
 *  - **internet** (mobilní data): relay/srflx přes coturn na VPS,
 *  - **localhost**: loopback.
 *
 * video-rtc.js navíc zkouší `webrtc`+`mse` paralelně a bere ten, co dřív dodá frame → uživatel
 * vidí obraz ~1 s (MSE-over-WS) a jakmile se naváže WebRTC, přepne se na něj (~0,2 s, živý okraj).
 * `hls` je poslední záchrana pro staré Safari bez ManagedMediaSource.
 *
 * Signaling jde přes autentizovanou app proxy `/api/foscam/stream/ws` (session cookie ověřená
 * proti DB i na WS upgrade). ICE config (STUN + per-user ephemeral TURN) se tahá z `/api/foscam/ice`
 * a injektuje do `pcConfig` PŘED spojením — video-rtc.js má natvrdo jen veřejný STUN.
 */

export type CameraStreamState = "loading" | "playing" | "stalled" | "error";

interface Props {
  slug: string;
  muted?: boolean;
  playsInline?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onState?: (s: CameraStreamState) => void;
  /**
   * Vydá samotný go2rtc element (ne vnitřní `<video>`). Drží `pc` a `mseCodecs`,
   * ze kterých `useWebrtcStats` čte skutečnou latenci a aktivní přenos.
   * Volá se s `null` při odpojení.
   */
  onEngine?: (el: VideoRTC | null) => void;
}

const ELEMENT_NAME = "go2rtc-video";

/** Registrace custom elementu jen jednou (idempotentně; HMR/StrictMode safe). */
function ensureDefined(): void {
  if (typeof window === "undefined" || !("customElements" in window)) return;
  if (!customElements.get(ELEMENT_NAME)) customElements.define(ELEMENT_NAME, VideoRTC);
}

/** Přiřazení forwardovaného refu (callback i objekt). */
function assignRef(ref: React.ForwardedRef<HTMLVideoElement>, value: HTMLVideoElement | null): void {
  if (typeof ref === "function") ref(value);
  else if (ref) (ref as React.MutableRefObject<HTMLVideoElement | null>).current = value;
}

export const CameraStream = React.forwardRef<HTMLVideoElement, Props>(function CameraStream(
  { slug, muted = true, playsInline = true, className, style, onState, onEngine },
  forwardedRef
) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  /** Vnitřní <video>. Drží se i mimo mount efekt, aby na něj mohl sahat efekt níž. */
  const videoElRef = useRef<HTMLVideoElement | null>(null);
  /** Klíče stylu zapsané minule — co zmizí z propu, musí se z elementu odebrat. */
  const prevStyleRef = useRef<React.CSSProperties>({});
  const [, setState] = useState<CameraStreamState>("loading");

  // onState + props v refu, ať efekt závisí jen na slugu (a nerestartuje stream při re-renderu).
  const onStateRef = useRef(onState);
  onStateRef.current = onState;
  const onEngineRef = useRef(onEngine);
  onEngineRef.current = onEngine;
  const propsRef = useRef({ muted, playsInline, className, style });
  propsRef.current = { muted, playsInline, className, style };

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    ensureDefined();

    let cancelled = false;

    const emit = (s: CameraStreamState) => {
      if (cancelled) return;
      setState(s);
      onStateRef.current?.(s);
    };
    const onPlaying = () => emit("playing");
    const onWaiting = () => emit("stalled");

    emit("loading");

    // ── 1) Vytvořit element + <video> SYNCHRONNĚ ─────────────────────────────
    // Musí být synchronní: CameraTile čte poměr stran z videa ve svém mount-efektu,
    // který běží HNED po dětských efektech. Kdyby video vzniklo až po async fetchi
    // ICE, ref by byl při čtení null a detekce poměru by spadla na default 16:9.
    const el = document.createElement(ELEMENT_NAME) as VideoRTC;
    el.background = false; // stream jen když je element v DOM
    el.visibilityCheck = false; // viditelnost řeší grid/spotlight, ne element sám
    el.media = "video"; // kamery jedou bez audia (go2rtc source má -an)
    el.mode = "webrtc,mse,hls"; // webrtc primárně, MSE ~1 s fallback, hls pro staré Safari
    el.style.display = "contents"; // wrapper je layoutově průhledný → <video> je box

    host.appendChild(el); // connectedCallback → oninit() vytvoří el.video

    const video = el.video;
    if (!video) {
      emit("error");
      return () => {
        cancelled = true;
        try {
          el.remove();
        } catch {
          /* už odpojeno */
        }
      };
    }

    const p = propsRef.current;
    video.controls = false;
    video.muted = p.muted ?? true;
    video.playsInline = p.playsInline ?? true;
    video.setAttribute("playsinline", ""); // iOS: bez atributu jde stream do fullscreenu
    // Přepsat inline 100%/100% z video-rtc.js → <video> se chová jako původní přímý <video>
    // (poměr stran v gridu, objectFit:contain ve fill režimu). Styl/třídu dává rodič.
    video.style.width = "";
    video.style.height = "";
    video.style.display = "block";
    video.className = p.className ?? "";
    if (p.style) Object.assign(video.style, p.style);

    video.addEventListener("playing", onPlaying);
    video.addEventListener("waiting", onWaiting);
    videoElRef.current = video;
    prevStyleRef.current = p.style ?? {};
    assignRef(forwardedRef, video);
    onEngineRef.current?.(el);

    // ── 2) Injektovat per-user ICE a teprve pak spustit spojení ──────────────
    void (async () => {
      let iceServers: RTCIceServer[] | undefined;
      try {
        const res = await getFoscamIce();
        iceServers = res.responseObject?.iceServers as RTCIceServer[] | undefined;
      } catch {
        /* /ice selhalo → zůstane default veřejný STUN z video-rtc.js (LAN/tailnet stále OK) */
      }
      if (cancelled) return;
      if (iceServers && iceServers.length > 0) {
        el.pcConfig = { ...el.pcConfig, iceServers };
      }
      // src až teď → spustí WS + trickle ICE. Same-origin → session cookie se přiloží.
      el.src = `${API_URL}/api/foscam/stream/ws?src=${encodeURIComponent(slug)}`;
    })();

    // ── cleanup ──────────────────────────────────────────────────────────────
    return () => {
      cancelled = true;
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("waiting", onWaiting);
      videoElRef.current = null;
      prevStyleRef.current = {};
      assignRef(forwardedRef, null);
      onEngineRef.current?.(null);
      el.background = false;
      try {
        el.ondisconnect(); // zavře ws + pc, uvolní video
      } catch {
        /* už zavřené */
      }
      try {
        el.remove();
      } catch {
        /* už odpojené */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  /**
   * Přenést změnu `className`/`style` na už existující <video>.
   *
   * Mount efekt výše je schválně závislý jen na `[slug]` (jinak by se restartoval
   * stream), takže sám o sobě zápis do elementu udělá jen jednou. Rodič ale obojí mění
   * při přepnutí režimu dlaždice — `fill ? "block h-full w-full" : "block w-full"`
   * a `fill ? { objectFit: "contain" } : undefined`. Bez tohohle efektu zůstane video
   * po přechodu grid → spotlight bez `h-full` i bez `objectFit`. U 16:9 kamery na 16:9
   * obrazovce to splyne, u 3:2 kamery (Sever) a na telefonu na výšku ne — a digitální
   * zoom navíc potřebuje, aby stav DOM odpovídal tomu, co si o něm myslí výpočet výřezu.
   *
   * POZOR: zapisují se jen konkrétní vlastnosti. Nikdy `cssText` ani `Object.assign`
   * nad celým `style` objektem — na tomtéž elementu si `transform`/`transformOrigin`
   * per-frame přepisuje digitální zoom a plošný zápis by mu je smazal.
   */
  useEffect(() => {
    const video = videoElRef.current;
    if (!video) return;
    video.className = className ?? "";
    const next = style ?? {};
    const styleMap = video.style as unknown as Record<string, string>;
    for (const key of Object.keys(prevStyleRef.current)) {
      if (!(key in next)) styleMap[key] = "";
    }
    Object.assign(video.style, next);
    prevStyleRef.current = next;
  }, [className, style]);

  // Host je layoutově průhledný (display:contents) — skutečný box je vnitřní <video>,
  // které nese className/style od rodiče (stejně jako dřív přímý <video>).
  return <div ref={hostRef} style={{ display: "contents" }} />;
});

export default CameraStream;
